/**
 * Color removal utilities for Magic Wand tool
 * Provides algorithms for removing specific colors and making pixels transparent
 */

/**
 * Check if a pixel matches the target color and add to start points
 */
function checkAndAddStartPoint(
  y,
  x,
  data,
  width,
  targetColor,
  tolerance,
  startPoints
) {
  const idx = (y * width + x) * 4;
  const r = data[idx];
  const g = data[idx + 1];
  const b = data[idx + 2];

  const dr = r - targetColor.r;
  const dg = g - targetColor.g;
  const db = b - targetColor.b;
  const distance = Math.sqrt(dr * dr + dg * dg + db * db);

  if (distance <= tolerance) {
    startPoints.push({ x, y });
  }
}

/**
 * Recursive flood-fill algorithm
 */
function floodFill(
  data,
  width,
  height,
  x,
  y,
  targetColor,
  tolerance,
  alphaMap,
  visited
) {
  const stack = [{ x, y }];

  while (stack.length > 0) {
    const { x: cx, y: cy } = stack.pop();

    // Check bounds
    if (cx < 0 || cx >= width || cy < 0 || cy >= height) continue;

    const pixelIndex = cy * width + cx;

    // Skip if already visited
    if (visited[pixelIndex]) continue;
    visited[pixelIndex] = 1;

    // Check color match
    const idx = pixelIndex * 4;
    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];

    const dr = r - targetColor.r;
    const dg = g - targetColor.g;
    const db = b - targetColor.b;
    const distance = Math.sqrt(dr * dr + dg * dg + db * db);

    if (distance <= tolerance) {
      // Mark for removal with smooth transition
      if (tolerance > 0) {
        const alpha = Math.max(0, Math.min(255, 255 * (distance / tolerance)));
        alphaMap[pixelIndex] = alpha;
      } else {
        alphaMap[pixelIndex] = 0;
      }

      // Add neighbors to stack (4-connected)
      stack.push({ x: cx + 1, y: cy });
      stack.push({ x: cx - 1, y: cy });
      stack.push({ x: cx, y: cy + 1 });
      stack.push({ x: cx, y: cy - 1 });

      // Optional: Add diagonal neighbors for 8-connected
      // stack.push({ x: cx + 1, y: cy + 1 });
      // stack.push({ x: cx - 1, y: cy - 1 });
      // stack.push({ x: cx + 1, y: cy - 1 });
      // stack.push({ x: cx - 1, y: cy + 1 });
    }
  }
}

/**
 * Create Gaussian kernel for blur operations
 */
function createGaussianKernel(radius) {
  const size = radius * 2 + 1;
  const kernel = [];
  const sigma = radius / 3;
  let sum = 0;

  for (let y = 0; y < size; y++) {
    kernel[y] = [];
    for (let x = 0; x < size; x++) {
      const dx = x - radius;
      const dy = y - radius;
      const value = Math.exp(-(dx * dx + dy * dy) / (2 * sigma * sigma));
      kernel[y][x] = value;
      sum += value;
    }
  }

  // Normalize kernel
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      kernel[y][x] /= sum;
    }
  }

  return kernel;
}

/**
 * Basic edge smoothing
 */
function basicEdgeSmooth(alphaMap, width, height, radius) {
  const result = new Uint8Array(alphaMap.length);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;

      if (alphaMap[idx] > 0) {
        let minAlpha = 255;
        let maxAlpha = 0;

        // Check neighboring pixels
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const ny = y + dy;
            const nx = x + dx;

            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              const nidx = ny * width + nx;
              minAlpha = Math.min(minAlpha, alphaMap[nidx]);
              maxAlpha = Math.max(maxAlpha, alphaMap[nidx]);
            }
          }
        }

        if (minAlpha < alphaMap[idx]) {
          // Smooth transition
          const distance = Math.sqrt((x % radius) ** 2 + (y % radius) ** 2);
          const factor = distance / radius;
          result[idx] = Math.floor(
            minAlpha + (alphaMap[idx] - minAlpha) * factor
          );
        } else {
          result[idx] = alphaMap[idx];
        }
      } else {
        result[idx] = 0;
      }
    }
  }

  return result;
}

/**
 * Gaussian blur for smooth edges
 */
function gaussianBlur(alphaMap, width, height, radius) {
  const result = new Uint8Array(alphaMap.length);
  const kernel = createGaussianKernel(radius);
  const kernelSize = kernel.length;
  const halfKernel = Math.floor(kernelSize / 2);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      let weightSum = 0;

      for (let ky = 0; ky < kernelSize; ky++) {
        for (let kx = 0; kx < kernelSize; kx++) {
          const ny = y + ky - halfKernel;
          const nx = x + kx - halfKernel;

          if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
            const weight = kernel[ky][kx];
            sum += alphaMap[ny * width + nx] * weight;
            weightSum += weight;
          }
        }
      }

      result[y * width + x] = Math.floor(sum / weightSum);
    }
  }

  return result;
}

/**
 * Feather edges for soft transitions
 */
function featherEdges(alphaMap, width, height, radius) {
  const result = new Uint8Array(alphaMap.length);
  const distanceMap = new Float32Array(alphaMap.length);

  // Calculate distance to nearest transparent pixel
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;

      if (alphaMap[idx] > 0) {
        let minDistance = radius + 1;

        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const ny = y + dy;
            const nx = x + dx;

            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              const nidx = ny * width + nx;
              if (alphaMap[nidx] === 0) {
                const distance = Math.sqrt(dx * dx + dy * dy);
                minDistance = Math.min(minDistance, distance);
              }
            }
          }
        }

        distanceMap[idx] = minDistance;
      } else {
        distanceMap[idx] = 0;
      }
    }
  }

  // Apply feathering based on distance
  for (let i = 0; i < alphaMap.length; i++) {
    if (distanceMap[i] === 0) {
      result[i] = 0;
    } else if (distanceMap[i] > radius) {
      result[i] = alphaMap[i];
    } else {
      // Smooth falloff
      const t = distanceMap[i] / radius;
      result[i] = Math.floor(alphaMap[i] * Math.pow(t, 1.5));
    }
  }

  return result;
}

/**
 * Aggressive smoothing combining multiple techniques
 */
function aggressiveSmooth(alphaMap, width, height, radius) {
  // Apply gaussian blur first
  let result = gaussianBlur(alphaMap, width, height, radius);

  // Then apply feathering
  result = featherEdges(result, width, height, Math.floor(radius / 2));

  // Apply contrast adjustment
  for (let i = 0; i < result.length; i++) {
    const value = result[i] / 255;
    const adjusted = Math.pow(value, 1.2) * 255;
    result[i] = Math.floor(Math.min(255, Math.max(0, adjusted)));
  }

  return result;
}

/**
 * Apply smoothing algorithm to alpha map
 */
function applySmoothingAlgorithm(alphaMap, width, height, type, radius) {
  switch (type) {
    case 'none':
      return alphaMap;
    case 'basic':
      return basicEdgeSmooth(alphaMap, width, height, radius);
    case 'gaussian':
      return gaussianBlur(alphaMap, width, height, radius);
    case 'feather':
      return featherEdges(alphaMap, width, height, radius);
    case 'aggressive':
      return aggressiveSmooth(alphaMap, width, height, radius);
    default:
      return alphaMap;
  }
}

/**
 * Flood-fill based color removal - only removes connected pixels
 * @param {Uint8ClampedArray} data - Image data array
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @param {Object} targetColor - Target color to remove
 * @param {number} tolerance - Color tolerance
 * @param {Object} startPoint - Starting point for flood-fill
 * @returns {Uint8Array} - Alpha map with connected pixels marked
 */
function floodFillColorRemoval(
  data,
  width,
  height,
  targetColor,
  tolerance,
  startPoint
) {
  const alphaMap = new Uint8Array(width * height);
  const visited = new Uint8Array(width * height);

  // Initialize all pixels as opaque
  alphaMap.fill(255);

  // If no start point provided, find all edge pixels that match the color
  const startPoints = [];

  if (startPoint) {
    startPoints.push(startPoint);
  } else {
    // Check all edge pixels (top, bottom, left, right edges)
    for (let x = 0; x < width; x++) {
      // Top edge
      checkAndAddStartPoint(
        0,
        x,
        data,
        width,
        targetColor,
        tolerance,
        startPoints
      );
      // Bottom edge
      checkAndAddStartPoint(
        height - 1,
        x,
        data,
        width,
        targetColor,
        tolerance,
        startPoints
      );
    }
    for (let y = 1; y < height - 1; y++) {
      // Left edge
      checkAndAddStartPoint(
        y,
        0,
        data,
        width,
        targetColor,
        tolerance,
        startPoints
      );
      // Right edge
      checkAndAddStartPoint(
        y,
        width - 1,
        data,
        width,
        targetColor,
        tolerance,
        startPoints
      );
    }
  }

  // Perform flood-fill from each starting point
  for (const point of startPoints) {
    floodFill(
      data,
      width,
      height,
      point.x,
      point.y,
      targetColor,
      tolerance,
      alphaMap,
      visited
    );
  }

  return alphaMap;
}

/**
 * Remove a specific color from image data with tolerance
 * @param {ImageData} imageData - The image data to process
 * @param {Object} targetColor - The color to remove {r, g, b}
 * @param {number} tolerance - Color matching tolerance (0-255)
 * @param {string} smoothingType - Type of edge smoothing to apply
 * @param {number} smoothRadius - Radius for smoothing operations
 * @param {boolean} connectedOnly - Only remove connected pixels (flood-fill mode)
 * @param {Object} startPoint - Starting point for connected mode {x, y}
 * @returns {ImageData} - Processed image data
 */
export function removeColorFromImageData(
  imageData,
  targetColor,
  tolerance = 50,
  smoothingType = 'gaussian',
  smoothRadius = 5,
  connectedOnly = false,
  startPoint = null
) {
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;

  // Create alpha map for pixels to be made transparent
  let alphaMap = new Uint8Array(width * height);

  if (connectedOnly) {
    // Connected mode: use flood-fill algorithm
    alphaMap = floodFillColorRemoval(
      data,
      width,
      height,
      targetColor,
      tolerance,
      startPoint
    );
  } else {
    // Standard mode: mark all matching pixels
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const pixelIndex = i / 4;

      // Calculate color distance using Euclidean distance
      const dr = r - targetColor.r;
      const dg = g - targetColor.g;
      const db = b - targetColor.b;
      const distance = Math.sqrt(dr * dr + dg * dg + db * db);

      // Determine alpha based on distance and tolerance
      if (distance <= tolerance) {
        // Smooth transition based on distance
        if (tolerance > 0) {
          const alpha = Math.max(
            0,
            Math.min(255, 255 * (distance / tolerance))
          );
          alphaMap[pixelIndex] = alpha;
        } else {
          alphaMap[pixelIndex] = 0;
        }
      } else {
        alphaMap[pixelIndex] = 255;
      }
    }
  }

  // Apply smoothing to alpha map
  const smoothedAlpha = applySmoothingAlgorithm(
    alphaMap,
    width,
    height,
    smoothingType,
    smoothRadius
  );

  // Apply the smoothed alpha values back to image data
  for (let i = 0; i < data.length; i += 4) {
    const pixelIndex = i / 4;
    data[i + 3] = smoothedAlpha[pixelIndex];
  }

  return imageData;
}

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color code
 * @returns {Object} RGB color object
 */
export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Convert RGB to hex color
 * @param {number} r - Red value
 * @param {number} g - Green value
 * @param {number} b - Blue value
 * @returns {string} Hex color code
 */
export function rgbToHex(r, g, b) {
  return (
    '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    b.toString(16).padStart(2, '0')
  );
}

/**
 * Get color at specific coordinates from canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @returns {Object} Color object with r, g, b, a values
 */
export function getColorAtPoint(ctx, x, y) {
  const pixel = ctx.getImageData(x, y, 1, 1).data;
  return {
    r: pixel[0],
    g: pixel[1],
    b: pixel[2],
    a: pixel[3],
  };
}
